// Identity & access DTOs — ADR-0012, PRD §3.16.
import { z } from 'zod';
import { uuidSchema } from './common.js';
export const loginRequestSchema = z.object({
    username: z.string().trim().min(1).max(64),
    password: z.string().min(1).max(256),
    deviceToken: z.string().min(1).max(256).optional(),
});
export const pinReauthRequestSchema = z.object({
    pin: z.string().regex(/^\d{4,6}$/),
});
export const currentUserSchema = z.object({
    id: uuidSchema,
    username: z.string(),
    displayNameAr: z.string(),
    displayNameEn: z.string().nullable(),
    locale: z.enum(['ar', 'en']),
    propertyId: uuidSchema,
    roles: z.array(z.string()),
    permissions: z.array(z.string()),
    mustChangePassword: z.boolean(),
    reauthValidUntil: z.iso.datetime().nullable(),
    /** 09 §4.7: TOTP enrolled and confirmed (optional on the node, recommended for managers) */
    mfaEnabled: z.boolean().default(false),
});
// ---------------------------------------------------------------- MFA (09 §4.7, P9-02)
/** password accepted but the account has TOTP: the client must call /auth/mfa/verify with the challenge */
export const mfaChallengeSchema = z.object({
    mfaRequired: z.literal(true),
    challengeId: uuidSchema,
    expiresAt: z.iso.datetime(),
});
export const loginResponseSchema = z.union([currentUserSchema, mfaChallengeSchema]);
/** 6-digit TOTP or a recovery code (xxxx-xxxx) */
export const totpCodeSchema = z
    .string()
    .trim()
    .regex(/^(\d{6}|[a-z2-7]{4}-?[a-z2-7]{4})$/i);
export const mfaVerifySchema = z.object({ challengeId: uuidSchema, code: totpCodeSchema });
export const mfaConfirmSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/),
});
export const mfaDisableSchema = z.object({ password: z.string().min(1) });
export const mfaSetupResponseSchema = z.object({
    secret: z.string(),
    otpauthUri: z.string(),
});
export const mfaConfirmResponseSchema = z.object({ recoveryCodes: z.array(z.string()).length(8) });
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(10).max(256), // BR-USR-03
});
export const setPinRequestSchema = z.object({
    password: z.string().min(1),
    pin: z.string().regex(/^\d{4,6}$/),
});
// ---------------------------------------------------------------- support access on the node (09 §9, P9-04)
export const supportGrantNodeDecisionSchema = z.object({
    note: z.string().trim().max(500).optional(),
});
// ---------------------------------------------------------------- PII retention (09 §10.3, P9-07)
export const retentionSettingsSchema = z.object({
    /** identity document number + image: wiped N months after the last departure (default 24) */
    identityDocumentMonths: z.number().int().min(1).max(240).default(24),
    /** guest profile without a stay for N months → anonymised (default 36) */
    guestProfileMonths: z.number().int().min(1).max(240).default(36),
    /** anonymisation is deferred while an invoice younger than this exists (default 10 years) [Requires Validation] */
    minFinancialYears: z.number().int().min(0).max(30).default(10),
});
//# sourceMappingURL=auth.js.map